import { useTranslation } from "react-i18next";
import { useEffect, type JSX } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import { DialogResult, DialogState, BindDialogReturn, useDialogState, bindDialog, DialogProps } from "../../../hooks/use-dialog-state/UseDialogState";
import { IeLynxObject } from "../../../common/models/eLynxObject";
import ResponsiveDialog from "../../../components/responsive-dialog/ResponsiveDialog";
import Form from "../../../components/forms/form/Form";
import FormPropertySection from "../../../components/forms/form-property-section/FormPropertySection";
import FormProperty from "../../../components/forms/form-property/FormProperty";
import FormInput from "../../../components/forms/form-input/FormInput";
import UnsavedChangesPrompt from "../../../components/unsaved-changes-prompt/UnsavedChangesPrompt";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { IConfiguration, IFileStoreInfo, ImportTypeEnum } from "../../../services/data-file-import-data-service/Models";
import FormSelect from "../../../components/forms/form-select/FormSelect";
import { getObjectTypeName } from "../../../common/utilities/GetObjectTypeName";
import { useOrganizationObjects } from "../../../queries/data-file-import/UseOranizationObjects";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { Box, MenuItem } from "@mui/material";
import LoadingZone from "../../../components/loading-zone/LoadingZone";
import DataFileImportDataService from "../../../services/data-file-import-data-service/DataFileImportDataService";
import { MAX_FILE_SIZE_BYTES } from "../DataFileImportUtils";
import { RestError } from "../../../services/rest-service/RestService";
import { AppNotificationTypeEnum } from "../../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../../contexts/app-notification-context/UseAppNotificationContext";
import htmlParse from 'html-react-parser';
import { getFilename } from "../../../common/utilities/AttachmentHelpers";

export type RunImportDialogInitValues = {
    groupID: number;
    configuration: IConfiguration
};

export type RunImportDialogReturnValues = {
    fileStoreInfo: IFileStoreInfo;
    object: IeLynxObject
};

export type RunImportDialogResult = DialogResult<RunImportDialogReturnValues>;
export type RunImportDialogProps = DialogProps<RunImportDialogInitValues, RunImportDialogResult>;

export type RunImportDialogFormValuesType = {
    fileName: string;
    objectID: number;
    fileStoreInfo: IFileStoreInfo | undefined;
};

const defaultFormValues: RunImportDialogFormValuesType = {
    fileName: '',
    objectID: -1,
    fileStoreInfo: undefined
};

export default function RunImportDialog({
    open,
    onClose,
    values,
}: RunImportDialogProps): JSX.Element {

    const { t } = useTranslation();
    const { confirm } = useConfirmation();
    const [, setAppNotificationState ] = useAppNotificationContext();

    const {data, status } = useQueryNotifications(useOrganizationObjects)({
        groupID: values?.groupID as number,
        objectType: values?.configuration.importObjectType as eLynxObjectTypeEnum,
        enabled: values !== undefined && values?.configuration.importType === ImportTypeEnum.Single
    });


    const form = useForm<RunImportDialogFormValuesType>({
        mode: "onChange",
        defaultValues: defaultFormValues
    });

    const {
        control,
        setValue,
        handleSubmit,
        formState: {
            isDirty,
            isValid
        },
        reset,
        //watch,
        //trigger
    } = form;


    const runForm = useWatch({ control: control }) as RunImportDialogFormValuesType;
    const _isValid = isValid && runForm.fileStoreInfo !== undefined;

    useEffect(() => {
        if (open) {

            if (values) {
                reset({
                    fileName: '',
                    objectID: -1
                });
            }
        } else {
            reset(defaultFormValues);
        }
    }, [reset, values, open]);

    async function onOkay(formdata: RunImportDialogFormValuesType) {

        const entity = data?.find(o => o.id === formdata.objectID && o.objectType === values?.configuration.importObjectType as eLynxObjectTypeEnum);

        onClose && onClose({
            reason: "ok",
            values: {
                fileStoreInfo: formdata.fileStoreInfo as IFileStoreInfo,
                object: entity as IeLynxObject
            }
        });
    }

    async function onCancel() {

        const canCancel = isDirty
            ? await getCancelConfirmation()
            : true;

        if (canCancel) {
            onClose && onClose({ reason: "cancel" });
        }
    }

    async function getFileTooLargeConfirmation() {
        await confirm({
            dialogTitle: t("dataFileImport.maxFileSizeExceededTitle"),
            confirmationText: t("dataFileImport.maxFileSizeExceeded"),
            yesButtonText: t("buttons.okay"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: false
        });
    }

    async function onFileError(error: RestError, file: File): Promise<boolean> {

        let askPermission: boolean = false;
        const errorCode = error.response?.data?.errorCode;
        let message: string | JSX.Element | undefined = undefined;
        let title: string | undefined = undefined;

        switch(errorCode) {
            case "ColumnCountMismatch":
                case "ColumnNameMismatch":
                case "DuplicateColumnNames":
                case "InvalidDateTime":
                case "InvalidValue":
                case "LessThanTwoColumns":
                case "MissingIdentifier":

                    title = t("dataFileImport.exceptions.importRunValidation.title") as string;
              
                    // message = this.hasModify ? i18n.t("dataFileImport.configuration.exceptions.importRunValidation.hasModifyCorrectionMessage", { message: message}) :
                    //     i18n.t("dataFileImport.configuration.exceptions.importRunValidation.correctionMessage", { message: message });

                    message = <Box>
                                {htmlParse(t("dataFileImport.exceptions.importRunValidation.hasModifyCorrectionMessage", { 
                                    message: error.response?.data.message
                                }))}
                              </Box>;
              
                    break;
              
                case "LimitedAccessToObjects":
                    askPermission = true;
                    break;
              
                case "NoAccessToObjects":
                    break;
              
                default:
                    break;
        }


        if (!askPermission) {

            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                show: true,
                title: title,
                message: message,
                hideContactInfo: message !== undefined,
                error: error
            });
        } else {

            // const dialogParams: Models.IConfirmationDialogModel = {
            //     dialogTitle: i18n.t("dataFileImport.modals.runImportConfirmation.title", { name: this.fileNameInput.value, objectTypeName: objectTypeName }),
            //     confirmationHtml: `<div class="info-dialog-content">${(i18n.t("dataFileImport.modals.runImportConfirmation.message", { objectTypeName: objectTypeName, message: message }))}</div>`,
            //     showYesButton: true,
            //     yesButtonText: i18n.t("dataFileImport.modals.runImportConfirmation.yesButton"),
            //     showNoButton: true,
            //     noButtonText: i18n.t("dataFileImport.modals.runImportConfirmation.noButton"),
            //     showOkButton: false,
            //     showCancelButton: false
            // };
            const objectTypeName = getObjectTypeName(values?.configuration.importObjectType as eLynxObjectTypeEnum);

            return (await confirm({
                dialogTitle: t("dataFileImport.runImportConfirmation.title", { 
                    name: getFilename(file.name), 
                    objectTypeName: objectTypeName
                }),
                //confirmationText: t("cancelConfirmation.confirmationText"),
                confirmationBodyComponent: <Box>
                    {t("dataFileImport.runImportConfirmation.message", { objectTypeName: objectTypeName, message: message })}

                </Box>,
                yesButtonText: t("dataFileImport.runImportConfirmation.yesButtonText"),
                noButtonText: t("dataFileImport.runImportConfirmation.noButtonText"),
                showConfirmationText: false,
                showYesButton: true,
                showNoButton: true,
                showCloseButton: true
               
            })) === "yes";

        }

        return false;
    }

    async function onFileChanged(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) : Promise<string | undefined> 
    {
        let name: string | undefined = undefined;
        const files = (e as React.ChangeEvent<HTMLInputElement>)?.target?.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (file.size < MAX_FILE_SIZE_BYTES) {
                try {

                    const fileInfo = await DataFileImportDataService.verifyAndSaveCsvFile(values?.groupID as number,
                        values?.configuration.id as number,
                        file);


                    setValue('fileStoreInfo', fileInfo.fileStoreInfo);

                } catch(error) {
                    const restError = error as RestError;

                    await onFileError(restError, file);

                    setTimeout(() => {
                        setValue('fileStoreInfo', undefined);
                        setValue('fileName', "", {
                            shouldDirty: true,
                            shouldValidate: true
                        });
                    }, 0);

                }
            } else {
                await getFileTooLargeConfirmation();

                setTimeout(() => {
                    setValue('fileName', "", {
                        shouldDirty: true,
                        shouldValidate: true
                    });
                }, 0);
            }
        }

        //resetFields();

        e.preventDefault();
        return name;
    }


    async function getCancelConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    function validateNotEmpty(value: string) {
        return value && value.length > 0;
    }


    return (
        <>
            <ResponsiveDialog
                title={t("dataFileImport.runImport")}
                enableOkay={isDirty && _isValid}
                okayLabel={t("buttons.okay")}
                open={open}
                onClose={
                    async (reason: string) => {
                        reason === "ok"
                            ? handleSubmit(async (data) => onOkay(data))()
                            : onCancel();
                    }
                }
            >
                <LoadingZone isLoading={values?.configuration.importType === ImportTypeEnum.Single && status === 'loading'}>
                    <Form form={form}>

                        <FormPropertySection label={t('dataFileImport.steps.fileInformation')}>

                            <FormProperty sx={{flexBasis: '50%!important', maxWidth: '50%!important'}}>

                                <FormInput
                                    name="fileName"
                                    label={t("attachmentDialog.file")}
                                    multiline={false}
                                    sx={{
                                        marginTop: 2,
                                        width: 1,
                                        marginBottom: 1
                                    }}
                                    rules={{
                                        required: true,
                                        validate: validateNotEmpty
                                    }}
                                    fileUpload
                                    fileAcceptTypes=".csv"
                                    onChange={onFileChanged}
                                    helperText={t('dataFileImport.validcsv')}
                                />

                                {/* <Alert severity="info">
                                    {t('dataFileImport.validcsv')}
                                </Alert> */}


                            </FormProperty>

                        </FormPropertySection>

                        {
                            values?.configuration.importType === ImportTypeEnum.Single &&

                            <FormPropertySection label={t('dataFileImport.importObject')}>
                                <FormProperty sx={{flexBasis: '50%!important', maxWidth: '50%!important'}}>
                                    <FormSelect
                                        name="objectID"
                                        label={getObjectTypeName(values.configuration.importObjectType, true)}
                                        sx={{ 
                                            width: 1,
                                            marginTop: 1,
                                            maxWidth: '400px'
                                        }}
                                    >
                                        <MenuItem value={-1} disabled={true}>

                                        </MenuItem>
                                        {
                                            data?.map((o) => 
                                                <MenuItem key={o.id} value={o.id}>
                                                    {o.displayText}
                                                </MenuItem>
                                            )
                                        }
                                    </FormSelect> 
                                </FormProperty>

                            </FormPropertySection>
                        
                        }

                    </Form>
                </LoadingZone>
            </ResponsiveDialog>

            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getCancelConfirmation}
            />
        </>
    );
}

export type RunImportDialogState = DialogState<RunImportDialogInitValues, RunImportDialogResult>;
export type BindRunImportDialogReturn = BindDialogReturn<RunImportDialogInitValues, RunImportDialogResult>;

export function useRunImportDialogState(): RunImportDialogState {
    return useDialogState();
}

export function bindRunImportDialog(props: RunImportDialogState): BindRunImportDialogReturn {
    return bindDialog(props);
}
